import { CalendarClock, PackageSearch } from 'lucide-react';
import type { PortalForecastRow } from '@proerp/shared';
import { Badge } from '@proerp/ui';
import { ArticleTrendChart } from '@/components/ArticleTrendChart';
import { DemandByMonthChart } from '@/components/DemandByMonthChart';
import { EmptyNote, ErrorCard, LoadingNote, PageHeader, SectionCard } from '@/components/PageLayout';
import { ReportPreviewButton } from '@/components/ReportPreviewButton';
import { StatTile } from '@/components/StatTile';
import { formatExpectedDate, formatQuantity, groupByExpectedDate } from '@/lib/formatForecast';
import { forecastKpis, formatQuantityByUnit } from '@/lib/portalMetrics';
import { usePortalForecast } from '@/lib/usePortalForecast';

/**
 * Bedarfsvorschau im Partnerportal (ADR 0085, Stufe 1): welche Mengen wir von
 * diesem Lieferanten erwarten.
 *
 * Aufbau von grob nach fein — Kennzahlen, Verlauf, dann die Positionen. Wer
 * nur wissen will „wann kommt was", ist nach zwei Sekunden fertig; wer die
 * einzelne Zeile braucht, scrollt.
 *
 * Gruppiert wird nach Termin, nicht nach Artikel: Das ist die Frage des
 * Lieferanten („was muss ich wann liefern können"). Der volle Zeithorizont ist
 * eine Entscheidung des Tech-Leads — keine Kappung nach vorne.
 */
export function ForecastPage() {
  const forecast = usePortalForecast();

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Bedarfsvorschau"
        title="Was wir von dir erwarten"
        subtitle="Die Mengen, die wir in den kommenden Monaten von dir erwarten. Eine Planung, keine Bestellung — verbindlich wird erst eine Bestellung."
        action={
          forecast.data?.rows.length ? (
            <ReportPreviewButton
              path="/forecast/pdf"
              title="Bedarfsvorschau"
              filename="Bedarfsvorschau.pdf"
            />
          ) : null
        }
      />

      {forecast.isPending && <LoadingNote />}

      {forecast.isError && (
        <ErrorCard
          title="Die Bedarfsvorschau ist gerade nicht abrufbar."
          error={forecast.error}
          onRetry={() => void forecast.refetch()}
        />
      )}

      {forecast.data && <ForecastBody data={forecast.data} />}
    </div>
  );
}

function ForecastBody({ data }: { data: NonNullable<ReturnType<typeof usePortalForecast>['data']> }) {
  /**
   * Zwei verschiedene Sachverhalte, zwei verschiedene Texte: „dein Zugang ist
   * keinem Lieferanten zugeordnet" ist ein Fall für uns, „gerade nichts
   * freigegeben" ist normal. Als eine gemeinsame Leermeldung wäre der erste
   * Fall für den Partner nicht erkennbar — und er würde nicht nachfragen.
   */
  if (!data.hasSupplierLink) {
    return (
      <EmptyNote icon={<PackageSearch className="size-5" aria-hidden />}>
        Dein Zugang ist noch keinem Lieferantenkonto zugeordnet. Bitte wende dich an deinen
        Ansprechpartner bei PROBIG — dann erscheinen hier deine Artikel.
      </EmptyNote>
    );
  }
  if (data.rows.length === 0) {
    return (
      <EmptyNote icon={<CalendarClock className="size-5" aria-hidden />}>
        Aktuell liegt für dich kein freigegebener Bedarf vor. Sobald wir planen, siehst du es hier.
      </EmptyNote>
    );
  }

  const kpis = forecastKpis(data.rows);
  const groups = groupByExpectedDate(data.rows);

  return (
    <div className="flex flex-col gap-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile
          label="Nächster Termin"
          value={kpis.nextDate ? formatExpectedDate(kpis.nextDate) : '—'}
          hint={kpis.nextDate ? formatQuantityByUnit(kpis.nextByUnit) : 'Noch kein Termin geplant'}
          icon={<CalendarClock className="size-4" aria-hidden />}
          tone="accent"
        />
        <StatTile
          label="Artikel"
          value={String(kpis.articleCount)}
          hint={`${kpis.positionCount} ${kpis.positionCount === 1 ? 'Position' : 'Positionen'}`}
          icon={<PackageSearch className="size-4" aria-hidden />}
        />
        <StatTile
          label="Termine"
          value={String(groups.filter((group) => group.expectedDate !== null).length)}
          hint={
            groups.some((group) => group.expectedDate === null)
              ? 'zusätzlich Positionen ohne Termin'
              : 'alle Positionen terminiert'
          }
          icon={<CalendarClock className="size-4" aria-hidden />}
        />
      </div>

      <SectionCard
        title="Verlauf"
        description="Bedarf je Monat, gestapelt nach Einheit. Mengen verschiedener Einheiten werden nie zusammengezählt."
      >
        <DemandByMonthChart rows={data.rows} />
      </SectionCard>

      <SectionCard
        title="Einzelner Artikel"
        description="Innerhalb eines Artikels ist die Einheit gleich — deshalb hier eine durchgehende Menge je Monat."
      >
        <ArticleTrendChart rows={data.rows} />
      </SectionCard>

      <section className="flex flex-col gap-6">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Alle Positionen
        </h2>
        {groups.map((group) => (
          <div key={group.expectedDate ?? 'ohne-termin'} className="flex flex-col gap-2">
            <h3 className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
              <CalendarClock className="size-4 text-muted-foreground" aria-hidden />
              {formatExpectedDate(group.expectedDate)}
              <Badge tone="neutral">
                {group.rows.length} {group.rows.length === 1 ? 'Position' : 'Positionen'}
              </Badge>
            </h3>
            <PositionList rows={group.rows} />
          </div>
        ))}
      </section>
    </div>
  );
}

/** Stabiler Schlüssel einer Position (eine Zeile je Artikel/Termin/Projekt). */
const rowKey = (row: PortalForecastRow) =>
  `${row.articleId}-${row.expectedDate ?? ''}-${row.projectText ?? ''}`;

/**
 * Die Positionen eines Termins.
 *
 * **Mobil zuerst:** Auf dem Telefon eine Liste von Karten, ab `sm` die Tabelle.
 * Vorher gab es nur die Tabelle in einem `overflow-x-auto` — auf 390 px hieß das
 * pro Termingruppe ein eigener seitlicher Scrollbalken, und die MENGE (die Zahl,
 * um die es dem Lieferanten geht) lag außerhalb des Bildes. Gemessen mit
 * `tools/design-review/portal-mobile-audit.mjs`: 16 waagrecht scrollende
 * Bereiche auf einer Seite.
 *
 * Die Karte führt daher mit Menge + Einheit und stellt die Bezeichnung
 * darunter; Artikelnummer und Projekt stehen als Fußzeile. Beide Darstellungen
 * zeigen dieselben Daten — eine ist nicht die gekürzte Fassung der anderen.
 */
function PositionList({ rows }: { rows: readonly PortalForecastRow[] }) {
  return (
    <>
      {/* Telefon: Karten. `sm:hidden`, damit die Tabelle ab Tablet übernimmt. */}
      <ul className="flex flex-col gap-2 sm:hidden">
        {rows.map((row) => (
          <li
            key={rowKey(row)}
            className="rounded-xl border border-border bg-card p-3 text-sm"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 font-medium text-foreground">{row.description}</p>
              <p className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-foreground">
                {formatQuantity(row.quantity)}
                {row.unit ? <span className="ml-1 font-normal text-muted-foreground">{row.unit}</span> : null}
              </p>
            </div>
            <dl className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <dt className="sr-only">Artikelnummer</dt>
                <dd className="font-mono">{row.articleNumber ?? '—'}</dd>
              </div>
              {row.projectText ? (
                <div className="flex min-w-0 items-center gap-1">
                  <span aria-hidden>·</span>
                  <dt className="sr-only">Projekt</dt>
                  <dd className="truncate">{row.projectText}</dd>
                </div>
              ) : null}
            </dl>
          </li>
        ))}
      </ul>

      {/* Ab Tablet: die Tabelle. Kein `overflow-x-auto` mehr — ab 640 px passen
          die fünf Spalten, und ein Scrollbalken, der nie zum Einsatz kommt,
          verschluckt nur den unteren Rahmen. */}
      <div className="hidden rounded-xl border border-border bg-card sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2.5 font-medium">Artikel</th>
              <th className="px-4 py-2.5 font-medium">Bezeichnung</th>
              <th className="px-4 py-2.5 font-medium">Projekt</th>
              <th className="px-4 py-2.5 text-right font-medium">Menge</th>
              <th className="px-4 py-2.5 font-medium">Einheit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className="border-b border-border transition-colors last:border-0 hover:bg-accent/40"
              >
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                  {row.articleNumber ?? '—'}
                </td>
                <td className="px-4 py-2.5 text-foreground">{row.description}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{row.projectText ?? '—'}</td>
                <td className="px-4 py-2.5 text-right font-medium tabular-nums text-foreground">
                  {formatQuantity(row.quantity)}
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">{row.unit ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
